import type { Paper } from "../types/api";
import { formatEvidenceLevel } from "./format";

export type SortKey = "title" | "rcs" | "level" | "cited" | "year" | "journalQ" | "final";
export type SortDir = "asc" | "desc";

const LEVEL_ORDER: Record<string, number> = {
  I: 1,
  II: 2,
  III: 3,
  IV: 4,
  V: 5,
};

function levelRank(level: string | undefined): number {
  if (!level) return 99;
  const m = level.match(/([IVX]+)/i);
  return m ? LEVEL_ORDER[m[1].toUpperCase()] ?? 99 : 99;
}

export function isOutlier(paper: Paper): boolean {
  return paper.title === "Unknown" || /^v$/i.test(paper.evidence_level ?? "") || paper.evidence_level === "Level V";
}

export function sortPapers(papers: Paper[], key: SortKey, dir: SortDir): Paper[] {
  const mul = dir === "asc" ? 1 : -1;
  return [...papers].sort((a, b) => {
    let cmp = 0;
    switch (key) {
      case "title":
        cmp = (a.title ?? "").localeCompare(b.title ?? "");
        break;
      case "rcs":
        cmp = (a.rcs_score ?? 0) - (b.rcs_score ?? 0);
        break;
      case "level":
        cmp = levelRank(a.evidence_level) - levelRank(b.evidence_level);
        break;
      case "cited":
        cmp = (a.citation_count ?? -1) - (b.citation_count ?? -1);
        break;
      case "year":
        cmp = (a.year ?? 0) - (b.year ?? 0);
        break;
      case "journalQ":
        cmp = (a.journal_quality ?? -1) - (b.journal_quality ?? -1);
        break;
      case "final":
        cmp = (a.final_score ?? a.rcs_score ?? 0) - (b.final_score ?? b.rcs_score ?? 0);
        break;
    }
    return cmp * mul;
  });
}

export function evidenceLevelCounts(papers: Paper[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const p of papers) {
    const label = formatEvidenceLevel(p.evidence_level);
    counts[label] = (counts[label] ?? 0) + 1;
  }
  return counts;
}

export function averageRcs(papers: Paper[]): number | null {
  const scores = papers.map((p) => p.rcs_score).filter((s): s is number => s != null);
  if (!scores.length) return null;
  return scores.reduce((a, b) => a + b, 0) / scores.length;
}

export function papersToCsv(papers: Paper[]): string {
  const headers = [
    "title",
    "journal",
    "rcs_score",
    "evidence_level",
    "citation_count",
    "year",
    "journal_quality",
    "final_score",
    "pmcid",
    "doi",
  ];
  const rows = papers.map((p) =>
    headers
      .map((h) => {
        const v = (p as Record<string, unknown>)[h];
        const s = v == null ? "" : String(v);
        return s.includes(",") || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
      })
      .join(",")
  );
  return [headers.join(","), ...rows].join("\n");
}

export function levelBadgeClass(level: string | undefined): string {
  const rank = levelRank(level);
  if (rank === 1) return "lvl-i";
  if (rank === 2) return "lvl-ii";
  if (rank === 3 || rank === 4) return "lvl-iii";
  return "lvl-v";
}
